import React, { useState, useEffect, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";

import ModalImage from "react-modal-image";
import api from "../../services/api";
import { getBackendUrl } from "../../config";

/**
 * URL que o Axios aceita em api.get (absoluta). Caminhos relativos exigem baseURL ou REACT_APP_BACKEND_URL.
 * Sem isso, o XHR dispara "Failed to execute 'open' on 'XMLHttpRequest': Invalid URL".
 */
function getFetchableAbsoluteUrl(mediaUrl) {
	if (!mediaUrl || typeof mediaUrl !== "string") return null;
	const u = mediaUrl.trim();
	if (!u) return null;
	if (u.startsWith("blob:") || u.startsWith("data:")) return null;
	if (u.startsWith("http://") || u.startsWith("https://")) {
		try {
			return new URL(u).href;
		} catch {
			return null;
		}
	}
	if (u.startsWith("/")) {
		const base = api.defaults.baseURL || getBackendUrl();
		if (!base || String(base).trim() === "") return null;
		const baseStr = String(base).replace(/\/$/, "");
		try {
			return new URL(u, `${baseStr}/`).href;
		} catch {
			return null;
		}
	}
	return null;
}

const useStyles = makeStyles(theme => ({
	messageMedia: {
		objectFit: "cover",
		width: 250,
		height: 200,
		borderTopLeftRadius: 8,
		borderTopRightRadius: 8,
		borderBottomLeftRadius: 8,
		borderBottomRightRadius: 8,
	},
}));

const ModalImageCors = ({ imageUrl, showRotate = true }) => {
	const classes = useStyles();
	const [fetching, setFetching] = useState(true);
	const [blobUrl, setBlobUrl] = useState("");
	const blobUrlRef = useRef("");

	useEffect(() => {
		blobUrlRef.current = blobUrl;
	}, [blobUrl]);

	useEffect(() => {
		return () => {
			if (blobUrlRef.current) {
				window.URL.revokeObjectURL(blobUrlRef.current);
			}
		};
	}, []);

	useEffect(() => {
		if (!imageUrl || typeof imageUrl !== 'string' || imageUrl.trim() === '') {
			setFetching(false);
			return;
		}

		// Validate URL format
		let validUrl = imageUrl;
		try {
			// If it's a relative URL, it should start with /
			if (!imageUrl.startsWith('http://') && !imageUrl.startsWith('https://') && !imageUrl.startsWith('/')) {
				setFetching(false);
				return;
			}
		} catch (err) {
			setFetching(false);
			return;
		}

		const requestUrl = getFetchableAbsoluteUrl(validUrl);
		if (!requestUrl) {
			setFetching(false);
			return;
		}

		setFetching(true);

		const fetchImage = async () => {
			try {
				const { data, headers } = await api.get(requestUrl, {
					responseType: "blob",
				});
				const url = window.URL.createObjectURL(
					new Blob([data], { type: headers["content-type"] })
				);
				setBlobUrl((prev) => {
					if (prev) window.URL.revokeObjectURL(prev);
					return url;
				});
				setFetching(false);
			} catch (err) {
				console.error("Error fetching image:", err);
				setFetching(false);
			}
		};
		fetchImage();
	}, [imageUrl]);

	// react-modal-image usa `src={small}` na miniatura; sem `small` a imagem fica quebrada em vários browsers.
	const displayUrl = fetching ? imageUrl : blobUrl || imageUrl;

	return (
		<ModalImage
			className={classes.messageMedia}
			small={displayUrl}
			smallSrcSet={displayUrl}
			medium={displayUrl}
			large={displayUrl}
			showRotate={showRotate}
			alt="image"
		/>
	);
};

export default ModalImageCors;
